import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
  Svg,
  Path,
} from "@react-pdf/renderer";
import type { IEstimatePayload } from "./types";
import { parseLabelGroups, ILabelGroup, parsePaymentPoliciesForPdf } from "../../../../../companyOutlet/helpers/labelGroupHelpers";
import { withS3Prefix } from "../../../../../../helpers/withS3Prefix";

// Register fonts
const NotoSansFont = require("../../../../../../ejs/fonts/NotoSans-Regular.ttf");
const NotoSansBold = require("../../../../../../ejs/fonts/NotoSans-Bold.ttf");

Font.register({
  family: "NotoSans",
  fonts: [
    { src: NotoSansFont, fontWeight: 400, fontStyle: "normal" },
    { src: NotoSansBold, fontWeight: 700, fontStyle: "normal" },
    { src: NotoSansFont, fontWeight: 400, fontStyle: "italic" },
    { src: NotoSansBold, fontWeight: 700, fontStyle: "italic" },
  ],
});

// ─── Design tokens matching the PDF ──────────────────────────────────────────
const PAGE_BG = "#EDEDED";
const CARD_BG = "#FFFFFF";
const DARK_BAR = "#464646";
const ACCENT_GOLD = "#c8a415";

// ─── Icon paths (Material-style, monochrome) ────────────────────────────────
const ICON_PATHS = {
  person: "M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z",
  phone: "M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z",
  info: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
  calendar: "M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zM19 8H5V6h14v2z",
  clock: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z",
  guests: "M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
  pin: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z",
  check: "M9 16.2l-3.5-3.5-1.4 1.4L9 19 20 8l-1.4-1.4z",
};

const Icon: React.FC<{ path: string; size?: number; color?: string }> = ({ path, size = 10, color = "#333" }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d={path} fill={color} />
  </Svg>
);

const TriangleBullet: React.FC<{ color?: string }> = ({ color = DARK_BAR }) => (
  <Svg width={7} height={8} viewBox="0 0 10 12" style={{ marginRight: 7, marginTop: 3, flexShrink: 0 } as any}>
    <Path d="M0 0 L10 6 L0 12 Z" fill={color} />
  </Svg>
);

const s = StyleSheet.create({
  page: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 24,
    fontFamily: "NotoSans",
    fontSize: 9,
    backgroundColor: PAGE_BG,
    color: "#222",
  },
  // ─── Header ───
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    backgroundColor: CARD_BG,
    paddingVertical: 18,
    paddingHorizontal: 20,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    marginBottom: 0,
  },
  companyName: { fontSize: 15, fontWeight: "bold", color: "#222", marginBottom: 3 },
  companyAddress: { fontSize: 8.5, color: "#666", lineHeight: 1.6 },
  logo: { width: 70, height: 55, objectFit: "contain" },
  // ─── Customer Bar ───
  customerBar: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: CARD_BG,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e0e0e0",
    paddingVertical: 9,
    paddingHorizontal: 20,
    marginBottom: 0,
    gap: 30,
  },
  customerItem: { flexDirection: "row", alignItems: "center", gap: 5 },
  customerLabel: { fontWeight: "bold", fontSize: 9, color: "#333" },
  customerValue: { fontSize: 9, color: "#111" },
  // ─── Content Card (white bg for body) ───
  contentCard: {
    backgroundColor: CARD_BG,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  // ─── Event Header ───
  eventHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: DARK_BAR,
    borderRadius: 5,
    marginBottom: 10,
    marginTop: 12,
    height: 32,
  },
  eventNameWrap: {
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 12,
    paddingRight: 14,
    height: "100%",
    borderRightWidth: 2,
    borderRightColor: ACCENT_GOLD,
  },
  eventName: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#fff",
  },
  eventTags: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    paddingLeft: 14,
    height: "100%",
  },
  eventTagItem: { flexDirection: "row", alignItems: "center", gap: 4 },
  eventTag: { fontSize: 8.5, color: "#ddd" },
  // ─── Items Table ───
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#bbb",
    paddingBottom: 5,
    paddingTop: 2,
    marginBottom: 2,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e0e0e0",
  },
  // Column widths for items table (7 columns)
  colItem: { width: "22%" },
  colQty: { width: "8%" },
  colPrice: { width: "13%" },
  colSubtotal: { width: "14%" },
  colDiscount: { width: "15%" },
  colGst: { width: "14%" },
  colTotal: { width: "14%", textAlign: "right" },
  thText: { fontSize: 8.5, fontWeight: "bold", color: "#111" },
  tdText: { fontSize: 8.5, color: "#333" },
  tdBold: { fontSize: 8.5, fontWeight: "bold", color: "#111" },
  // ─── Stay Section ───
  stayHeader: {
    backgroundColor: DARK_BAR,
    borderRadius: 5,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginTop: 14,
    marginBottom: 8,
  },
  stayHeaderText: { fontSize: 11, fontWeight: "bold", color: "#fff" },
  // Stay table columns (10 columns)
  stColName: { width: "14%" },
  stColCheckIn: { width: "11%" },
  stColCheckOut: { width: "11%" },
  stColRooms: { width: "7%" },
  stColNights: { width: "7%" },
  stColPrice: { width: "10%" },
  stColSubtotal: { width: "11%" },
  stColDiscount: { width: "11%" },
  stColGst: { width: "10%" },
  stColTotal: { width: "8%", textAlign: "right" },
  // ─── Financial Summary ───
  financialBox: {
    borderWidth: 0.5,
    borderColor: "#ccc",
    borderRadius: 4,
    marginTop: 16,
  },
  financialRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderBottomWidth: 0.5,
    borderBottomColor: "#e8e8e8",
  },
  financialLabel: { fontSize: 9.5, color: "#444" },
  financialValue: { fontSize: 9.5, fontWeight: "bold", color: "#111" },
  // ─── Remaining Balance Bar ───
  remainingBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: DARK_BAR,
    borderRadius: 5,
    paddingVertical: 9,
    paddingHorizontal: 14,
    marginTop: 6,
  },
  remainingLabel: { fontSize: 10, fontWeight: "bold", color: "#fff" },
  remainingAmount: { fontSize: 11, fontWeight: "bold", color: "#fff" },
  // ─── Bank Details ───
  bankTitle: { fontSize: 10, fontWeight: "bold", marginBottom: 6, color: "#111" },
  bankText: { fontSize: 8.5, color: "#444", lineHeight: 1.8 },
  qrImage: { width: 75, height: 75, objectFit: "contain" },
  // ─── Gallery (Package Highlights) ───
  galleryRow: { flexDirection: "row", gap: 8, marginBottom: 4 },
  galleryImg: { flex: 1, height: 110, objectFit: "cover", borderRadius: 4 },
  // ─── Outlet Sections (Inclusions, Exclusions, Policies) ───
  sectionBar: {
    backgroundColor: DARK_BAR,
    borderRadius: 5,
    paddingVertical: 8,
    paddingHorizontal: 14,
    marginTop: 18,
    marginBottom: 10,
  },
  sectionBarText: { fontSize: 10, fontWeight: "bold", color: "#fff" },
  sectionSubHeading: { fontSize: 9.5, fontWeight: "bold", color: "#222", marginTop: 8, marginBottom: 6 },
  checkItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 4,
    paddingLeft: 4,
  },
  checkMark: { marginRight: 7, marginTop: 2 },
  checkText: { fontSize: 8.5, color: "#333", flex: 1, lineHeight: 1.5 },
  policyText: { fontSize: 8.5, color: "#333", lineHeight: 1.6, marginBottom: 6 },
  twoColumnRow: { flexDirection: "row", gap: 24 },
  twoColumnCol: { flex: 1 },
  // ─── Signatures ───
  signatures: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 36,
    paddingHorizontal: 20,
  },
  sigBlock: { alignItems: "center" },
  sigLine: { width: 150, borderTopWidth: 0.5, borderTopColor: "#333", marginBottom: 6 },
  sigLabel: { fontSize: 9, fontWeight: "bold", color: "#222" },
  // ─── Footer ───
  footerNote: { textAlign: "center", marginTop: 18, fontSize: 7.5, color: "#888" },
  footerCompany: { textAlign: "center", marginTop: 8 },
  footerCompanyName: { fontSize: 10, fontWeight: "bold", color: "#222" },
  footerCompanyAddress: { fontSize: 7.5, color: "#666", marginTop: 2 },
});

// ─── Helper: strip HTML tags and split into lines ────────────────────────────
function htmlToLines(html: string): string[] {
  if (!html || typeof html !== "string") return [];
  // Replace <br>, <br/>, </p>, </li> with newline markers
  let text = html.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<\/p>/gi, "\n");
  text = text.replace(/<\/li>/gi, "\n");
  // Remove all remaining HTML tags
  text = text.replace(/<[^>]*>/g, "");
  // Decode common HTML entities
  text = text.replace(/&amp;/g, "&");
  text = text.replace(/&lt;/g, "<");
  text = text.replace(/&gt;/g, ">");
  text = text.replace(/&nbsp;/g, " ");
  text = text.replace(/&quot;/g, '"');
  // Split into lines, trim, filter empty
  return text.split("\n").map((l) => l.trim()).filter(Boolean);
}

function hasContent(val: any): boolean {
  if (!val) return false;
  if (Array.isArray(val)) return val.length > 0;
  if (typeof val === "string") return val.trim() !== "";
  return Boolean(val);
}

function parseJsonArray(val: any): any[] {
  if (Array.isArray(val)) return val;
  if (typeof val === "string" && val.trim() !== "") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
    } catch { }
  }
  return [];
}

// Render a group's label + checklist items
const LabelGroupBlock: React.FC<{ group: ILabelGroup }> = ({ group }) => (
  <View>
    {group.label.trim() && <Text style={s.sectionSubHeading}>{group.label.toUpperCase()}</Text>}
    {group.items.filter((i) => i.value.trim()).map((item) => (
      <View style={s.checkItem} key={item.id} wrap={false}>
        <View style={s.checkMark}><Icon path={ICON_PATHS.check} size={9} color="#333" /></View>
        <Text style={s.checkText}>{item.value}</Text>
      </View>
    ))}
  </View>
);

// Render label groups two-per-row (matches PDF's SETUP/DÉCOR side-by-side layout)
const LabelGroupsSection: React.FC<{ raw: any }> = ({ raw }) => {
  const groups = parseLabelGroups(raw).filter(
    (g) => g.label.trim() || g.items.some((i) => i.value.trim())
  );
  if (groups.length === 0) return null;

  const rows: React.ReactNode[] = [];
  for (let i = 0; i < groups.length; i += 2) {
    const pair = groups.slice(i, i + 2);
    if (pair.length === 2) {
      rows.push(
        <View style={s.twoColumnRow} key={`row-${i}`} wrap={false}>
          <View style={s.twoColumnCol}><LabelGroupBlock group={pair[0]} /></View>
          <View style={s.twoColumnCol}><LabelGroupBlock group={pair[1]} /></View>
        </View>
      );
    } else {
      rows.push(
        <View key={`row-${i}`} wrap={false}>
          <LabelGroupBlock group={pair[0]} />
        </View>
      );
    }
  }
  return <>{rows}</>;
};

// ─── Component ───────────────────────────────────────────────────────────────

interface IQuotationDocumentProps {
  data: IEstimatePayload[];
  imageCache?: Record<string, string>;
  appLogo?: string;
  appName?: string;
}

export const QuotationDocument: React.FC<IQuotationDocumentProps> = ({ data, imageCache = {}, appLogo, appName }) => {
  if (!data || data.length === 0) return null;

  const company = data[0];
  let paymentQr: any[] = [];
  try {
    paymentQr = JSON.parse(company.payment_qr || "[]");
  } catch { }
  const gallery = parseJsonArray(company.gallery);
  const packageHighlights = parseJsonArray(company.attachments)
    .map((att: any) => (typeof att === "string" ? att : att?.path))
    .filter(Boolean);
  const allStayDetails = data.flatMap((e) => e.stay_details || []).filter(Boolean);

  // Helper to resolve image URL through pre-fetched cache, with cache-bust for CORS
  const resolveImg = (url: string) => {
    if (!url) return "";
    const full = withS3Prefix(url);
    if (imageCache[full]) return imageCache[full];
    return full + "?x-cache-bust=pdf";
  };

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* ═══ HEADER (fixed on every page) ═══ */}
        <View style={s.header} fixed>
          <View>
            <Text style={s.companyName}>{company.company_name}</Text>
            <Text style={s.companyAddress}>
              {company.billing_address_1},{"\n"}
              {company.billing_address_2} {company.billing_city}, {company.billing_state}, {company.billing_zip_code}
            </Text>
          </View>
          {company.logo ? <Image src={resolveImg(company.logo)} style={s.logo} /> : null}
        </View>

        {/* ═══ CUSTOMER INFO BAR ═══ */}
        <View style={s.customerBar}>
          <View style={s.customerItem}>
            <Icon path={ICON_PATHS.person} size={10} color="#333" />
            <Text style={s.customerLabel}>Guest Name: </Text>
            <Text style={s.customerValue}>{company.name_of_guest}</Text>
          </View>
          <View style={s.customerItem}>
            <Icon path={ICON_PATHS.phone} size={10} color="#333" />
            <Text style={s.customerLabel}>Contact No: </Text>
            <Text style={s.customerValue}>{company.country_code} {company.contact_number_one}</Text>
          </View>
          <View style={s.customerItem}>
            <Icon path={ICON_PATHS.info} size={10} color="#333" />
            <Text style={s.customerLabel}>Estimate Number: </Text>
            <Text style={s.customerValue}>{company.estimate_number}</Text>
          </View>
        </View>

        {/* ═══ CONTENT CARD ═══ */}
        <View style={s.contentCard}>

          {/* ═══ STAY DETAILS (all events combined, shown above the events) ═══ */}
          {allStayDetails.length > 0 && (
            <View style={{ marginBottom: 4 }} wrap={false}>
              <View style={[s.stayHeader, { marginTop: 0 }]}>
                <Text style={s.stayHeaderText}>Stay Details</Text>
              </View>
              {/* Stay Table Header */}
              <View style={s.tableHeader}>
                <Text style={[s.thText, s.stColName]}>Room Name</Text>
                <Text style={[s.thText, s.stColCheckIn]}>Check in</Text>
                <Text style={[s.thText, s.stColCheckOut]}>Check Out</Text>
                <Text style={[s.thText, s.stColRooms]}>Rooms</Text>
                <Text style={[s.thText, s.stColNights]}>Nights</Text>
                <Text style={[s.thText, s.stColPrice]}>Price</Text>
                <Text style={[s.thText, s.stColSubtotal]}>Subtotal</Text>
                <Text style={[s.thText, s.stColDiscount]}>Discount</Text>
                <Text style={[s.thText, s.stColGst]}>GST</Text>
                <Text style={[s.thText, s.stColTotal]}>Total</Text>
              </View>
              {/* Stay Table Rows */}
              {allStayDetails.map((stay, si) => (
                <View style={s.tableRow} key={si}>
                  <Text style={[s.tdText, s.stColName]}>{stay.room_name}</Text>
                  <Text style={[s.tdText, s.stColCheckIn]}>{stay.check_in}</Text>
                  <Text style={[s.tdText, s.stColCheckOut]}>{stay.check_out}</Text>
                  <Text style={[s.tdText, s.stColRooms]}>{String(stay.rooms).padStart(2, "0")}</Text>
                  <Text style={[s.tdText, s.stColNights]}>{String(stay.nights).padStart(2, "0")}</Text>
                  <Text style={[s.tdText, s.stColPrice]}>{stay.price}</Text>
                  <Text style={[s.tdText, s.stColSubtotal]}>{stay.sub_total}</Text>
                  <Text style={[s.tdText, s.stColDiscount]}>{(stay as any).discount_amount || "₹0"} ({stay.discount || 0}{stay.discount_mode === "percentage" ? "%" : ""})</Text>
                  <Text style={[s.tdText, s.stColGst]}>{stay.gst_amount} ({stay.gst_percentage || 0}%)</Text>
                  <Text style={[s.tdBold, s.stColTotal]}>{stay.total}</Text>
                </View>
              ))}
            </View>
          )}

          {/* ═══ PER-EVENT SECTIONS ═══ */}
          {data.map((event, idx) => (
            <View key={idx} wrap={false}>
              {/* Event Header Bar */}
              <View style={s.eventHeader}>
                <View style={s.eventNameWrap}>
                  <Text style={s.eventName}>
                    {event.event_type === "Other Social" && event.other_event_name
                      ? event.other_event_name
                      : event.event_type}
                  </Text>
                </View>
                <View style={s.eventTags}>
                  <View style={s.eventTagItem}>
                    <Icon path={ICON_PATHS.calendar} size={9.5} color="#ddd" />
                    <Text style={s.eventTag}>{event.function_date}</Text>
                  </View>
                  <View style={s.eventTagItem}>
                    <Icon path={ICON_PATHS.clock} size={9.5} color="#ddd" />
                    <Text style={s.eventTag}>{event.event_time}</Text>
                  </View>
                  <View style={s.eventTagItem}>
                    <Icon path={ICON_PATHS.guests} size={9.5} color="#ddd" />
                    <Text style={s.eventTag}>{Number(event.number_of_guests).toLocaleString("en-IN")}+</Text>
                  </View>
                  <View style={s.eventTagItem}>
                    <Icon path={ICON_PATHS.pin} size={9.5} color="#ddd" />
                    <Text style={s.eventTag}>{event.preferred_venue}</Text>
                  </View>
                </View>
              </View>

              {/* Items Charges Table */}
              {event.items_charges && event.items_charges.length > 0 && (
                <View style={{ marginBottom: 4 }}>
                  {/* Table Header */}
                  <View style={s.tableHeader}>
                    <Text style={[s.thText, s.colItem]}>Items</Text>
                    <Text style={[s.thText, s.colQty]}>QTY</Text>
                    <Text style={[s.thText, s.colPrice]}>Unit Price</Text>
                    <Text style={[s.thText, s.colSubtotal]}>Subtotal</Text>
                    <Text style={[s.thText, s.colDiscount]}>Discount</Text>
                    <Text style={[s.thText, s.colGst]}>Tax</Text>
                    <Text style={[s.thText, s.colTotal]}>Total</Text>
                  </View>
                  {/* Table Rows */}
                  {event.items_charges.map((item, i) => (
                    <View style={s.tableRow} key={i}>
                      <Text style={[s.tdText, s.colItem]}>{item.name}</Text>
                      <Text style={[s.tdText, s.colQty]}>{item.count}</Text>
                      <Text style={[s.tdText, s.colPrice]}>{item.amount}</Text>
                      <Text style={[s.tdText, s.colSubtotal]}>{item.sub_total}</Text>
                      <Text style={[s.tdText, s.colDiscount]}>
                        {item.discount_amount || "₹0"} ({item.discount}{item.discount_mode === "percentage" ? "%" : ""})
                      </Text>
                      <Text style={[s.tdText, s.colGst]}>
                        {item.tax_type === "VAT"
                          ? `${item.vat_amount || "₹0.00"} (${item.vat_percentage || 0}% VAT)`
                          : `${item.gst_amount} (${item.gst_percentage}% GST)`}
                      </Text>
                      <Text style={[s.tdBold, s.colTotal]}>{item.total}</Text>
                    </View>
                  ))}
                </View>
              )}
            </View>
          ))}

          {/* ═══ FINANCIAL SUMMARY ═══ */}
          {(() => {
            const last = data[data.length - 1];
            // Combined totals for all events come from group_summary; fall back to the
            // last record for responses that don't include it yet
            const summary = last.group_summary ?? data[0].group_summary;
            const subTotal = summary?.sub_total ?? last.sub_total;
            const effectiveTotal = summary?.sub_total_less_discount ?? last.sub_total_less_discount;
            const totalGst = summary?.total_gst ?? last.total_gst;
            const totalVat = summary?.total_vat ?? last.total_vat;
            const totalDiscount = summary?.total_discount ?? last.total_discount;
            const grandTotal = summary?.grand_total ?? last.grand_total;
            const amountPaid = summary?.total_amount_paid ?? last.total_amount_paid;
            const remainingBalance = summary?.remaining_balance ?? last.remaining_balance;
            // group_summary amounts arrive pre-formatted ("₹100.00"); older fields may be raw numbers
            const formatAmount = (v: string | number | null | undefined) =>
              typeof v === "string" && v.includes("₹") ? v : `₹${Number(v || 0).toLocaleString("en-IN")}`;
            return (
              <>
                <View style={s.financialBox}>
                  <View style={s.financialRow}>
                    <Text style={s.financialLabel}>Sub Total</Text>
                    <Text style={s.financialValue}>{subTotal}</Text>
                  </View>
                  <View style={s.financialRow}>
                    <Text style={s.financialLabel}>Effective total</Text>
                    <Text style={s.financialValue}>{effectiveTotal}</Text>
                  </View>
                  <View style={s.financialRow}>
                    <Text style={s.financialLabel}>Total GST</Text>
                    <Text style={s.financialValue}>{totalGst}</Text>
                  </View>
                  {Number(String(totalVat ?? "").replace(/[^0-9.-]/g, "")) > 0 && (
                    <View style={s.financialRow}>
                      <Text style={s.financialLabel}>Total VAT</Text>
                      <Text style={s.financialValue}>{totalVat}</Text>
                    </View>
                  )}
                  <View style={s.financialRow}>
                    <Text style={s.financialLabel}>Total Discount</Text>
                    <Text style={s.financialValue}>{totalDiscount}</Text>
                  </View>
                  <View style={s.financialRow}>
                    <Text style={s.financialLabel}>Grand Total</Text>
                    <Text style={s.financialValue}>{grandTotal}</Text>
                  </View>
                  <View style={{ ...s.financialRow, borderBottomWidth: 0 }}>
                    <Text style={s.financialLabel}>Amount Paid</Text>
                    <Text style={s.financialValue}>{formatAmount(amountPaid)}</Text>
                  </View>
                </View>
                <View style={s.remainingBar}>
                  <Text style={s.remainingLabel}>Remaining Balance</Text>
                  <Text style={s.remainingAmount}>{formatAmount(remainingBalance)}</Text>
                </View>
              </>
            );
          })()}

          {/* ═══ BANK DETAILS ═══ */}
          {(() => {
            // API can send null, "", or the literal string "null" — skip the card entirely then
            const rawPolicies = typeof company.payment_mode_policies === "string" ? company.payment_mode_policies.trim() : "";
            if (!rawPolicies || rawPolicies === "null" || rawPolicies === "undefined") return null;
            const parsedPolicies = parsePaymentPoliciesForPdf(rawPolicies);
            const hasBankLines = parsedPolicies.bankLines.length > 0;
            return (
              <View style={{ borderWidth: 0.5, borderColor: "#ccc", borderRadius: 4, marginTop: 16, padding: 14 }} wrap={false}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.bankTitle}>{parsedPolicies.heading || "BANK DETAILS"}</Text>
                    {hasBankLines ? (
                      parsedPolicies.bankLines.map((line, i) => (
                        <Text style={s.bankText} key={`bank-${i}`}>{line}</Text>
                      ))
                    ) : (
                      <Text style={s.bankText}>Contact for bank details</Text>
                    )}
                  </View>
                  {paymentQr.length > 0 && (
                    <Image
                      src={resolveImg(typeof paymentQr[0] === "string" ? paymentQr[0] : (paymentQr[0]?.path || paymentQr[0]?.url || ""))}
                      style={s.qrImage}
                    />
                  )}
                </View>
                {parsedPolicies.paymentTerms.length > 0 && (
                  <View style={{ marginTop: 10, paddingTop: 10, borderTopWidth: 0.5, borderTopColor: "#e8e8e8" }}>
                    {parsedPolicies.paymentTerms.map((line, i) => (
                      <View style={s.checkItem} key={`pt-${i}`} wrap={false}>
                        <TriangleBullet color="#333" />
                        <Text style={s.checkText}>{line}</Text>
                      </View>
                    ))}
                  </View>
                )}
              </View>
            );
          })()}

          {/* ═══ GALLERY ═══ */}
          {gallery.length > 0 && (
            <View>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText} minPresenceAhead={60}>Gallery</Text>
              </View>
              <View style={s.galleryRow} wrap={false}>
                {gallery.slice(0, 4).map((img, i) => (
                  <Image key={`gal-${i}`} src={resolveImg(typeof img === "string" ? img : "")} style={s.galleryImg} />
                ))}
              </View>
            </View>
          )}

          {/* ═══ PACKAGE HIGHLIGHTS (lead attachments) ═══ */}
          {packageHighlights.length > 0 && (
            <View>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText} minPresenceAhead={60}>Package Highlights</Text>
              </View>
              {[0, 3].map((start) => {
                const row = packageHighlights.slice(start, start + 3);
                if (row.length === 0) return null;
                return (
                  <View style={[s.galleryRow, start > 0 ? { marginTop: 8 } : {}]} key={`ph-row-${start}`} wrap={false}>
                    {row.map((img: string, i: number) => (
                      <Image key={`ph-${start + i}`} src={resolveImg(img)} style={s.galleryImg} />
                    ))}
                  </View>
                );
              })}
            </View>
          )}

          {/* ═══ ABOUT BANQUET HALL ═══ */}
          {hasContent(company.banquet_description) && (
            <View>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText} minPresenceAhead={30}>About Banquet Hall</Text>
              </View>
              {htmlToLines(company.banquet_description).map((line, i) => (
                <View style={s.checkItem} key={`bd-${i}`} wrap={false}>
                  <TriangleBullet />
                  <Text style={s.checkText}>{line}</Text>
                </View>
              ))}
            </View>
          )}

          {/* ═══ OUR PROPOSAL ═══ */}
          {hasContent(company.proposal) && (
            <View>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText} minPresenceAhead={30}>Our Proposal</Text>
              </View>
              {htmlToLines(company.proposal).map((line, i) => (
                <View style={s.checkItem} key={`pr-${i}`} wrap={false}>
                  <TriangleBullet />
                  <Text style={s.checkText}>{line}</Text>
                </View>
              ))}
            </View>
          )}

          {/* ═══ INCLUSIONS ═══ (heading + groups move as one atomic unit — a lone
              minPresenceAhead on the heading isn't enough once a group's checklist
              runs past that reserved height, and the heading ends up orphaned) */}
          {hasContent(company.inclusion) && (
            <View wrap={false}>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText}>Inclusions</Text>
              </View>
              <LabelGroupsSection raw={company.inclusion} />
            </View>
          )}

          {/* ═══ EXCLUSIONS ═══ same atomic heading+groups treatment as Inclusions */}
          {hasContent(company.exclusion) && (
            <View wrap={false}>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText}>Exclusions</Text>
              </View>
              <LabelGroupsSection raw={company.exclusion} />
            </View>
          )}

          {/* ═══ COMPANY POLICIES & TERMS ═══ */}
          {hasContent(company.policies) && (
            <View>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText} minPresenceAhead={30}>Company Policies & Terms</Text>
              </View>
              {htmlToLines(company.policies).map((line, i) => (
                <View style={s.checkItem} key={`pol-${i}`} wrap={false}>
                  <View style={s.checkMark}><Icon path={ICON_PATHS.check} size={9} color="#333" /></View>
                  <Text style={s.checkText}>{line}</Text>
                </View>
              ))}
            </View>
          )}

          {/* ═══ CANCELLATION POLICIES ═══ */}
          {hasContent(company.cancellation_policies) && (
            <View>
              <View style={s.sectionBar}>
                <Text style={s.sectionBarText} minPresenceAhead={30}>Cancellation Policies</Text>
              </View>
              {htmlToLines(company.cancellation_policies).map((line, i) => (
                <View style={s.checkItem} key={`cp-${i}`} wrap={false}>
                  <TriangleBullet />
                  <Text style={s.checkText}>{line}</Text>
                </View>
              ))}
            </View>
          )}

          {/* ═══ SIGNATURES ═══ */}
          <View style={s.signatures}>
            <View style={s.sigBlock}>
              <View style={s.sigLine} />
              <Text style={s.sigLabel}>Guest's Signature</Text>
            </View>
            <View style={s.sigBlock}>
              <View style={s.sigLine} />
              <Text style={s.sigLabel}>Reservation</Text>
            </View>
          </View>

          {/* ═══ FOOTER ═══ */}
        </View>{/* close contentCard */}
        <Text style={s.footerNote}>Note: This quotation is provided for estimation purposes only and is not a valid tax invoice</Text>
        <View style={s.footerCompany}>
          <Text style={s.footerCompanyName}>{company.company_name}</Text>
          <Text style={s.footerCompanyAddress}>
            {company.billing_address_1}, {company.billing_address_2}{"\n"}
            {company.billing_city}, {company.billing_state}, {company.billing_zip_code}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
